import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import { CheckCircle, Celebration } from "@mui/icons-material";

const OrderConfirmation = () => {
  const [orderDetails, setOrderDetails] = useState({
    orderId: "",
    date: "",
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    lashLength: 0,
    curlType: "",
    thickness: "",
    innerLength: 0,
    centerLength: 0,
    outerLength: 0,
    arReviewTemplateNames: [],
    selectedProducts: [],
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadOrderDetails = async () => {
      try {
        setLoading(true);

        const orderId = localStorage.getItem("orderId");
        const arReviewTemplates = JSON.parse(
          localStorage.getItem("ar_review_templates") || "[]"
        );
        const additionalProducts = JSON.parse(
          localStorage.getItem("additional_products") || "[]"
        );

        const sessionData = {
          first_name: localStorage.getItem("first_name"),
          last_name: localStorage.getItem("last_name"),
          email: localStorage.getItem("email"),
          mobile: localStorage.getItem("mobile"),
          lash_length_mm: parseFloat(
            localStorage.getItem("lash_length") || "0"
          ),
          curl_type: localStorage.getItem("curl_type"),
          thickness: localStorage.getItem("thickness"),
          inner_length: parseFloat(localStorage.getItem("inner_length") || "0"),
          center_length: parseFloat(
            localStorage.getItem("center_length") || "0"
          ),
          outer_length: parseFloat(localStorage.getItem("outer_length") || "0"),
          ar_review_template_names: arReviewTemplates.map((template) => template.name),
          selected_products: additionalProducts.map((product) => ({
            id: product.id,
            title: product.title,
          })),
        };

        setOrderDetails({
          orderId: orderId || "N/A",
          date: new Date().toLocaleString(),
          firstName: sessionData.first_name || "N/A",
          lastName: sessionData.last_name || "N/A",
          email: sessionData.email || "N/A",
          mobile: sessionData.mobile || "N/A",
          lashLength: sessionData.lash_length_mm || 0,
          curlType: sessionData.curl_type || "N/A",
          thickness: sessionData.thickness || "N/A",
          innerLength: sessionData.inner_length || 0,
          centerLength: sessionData.center_length || 0,
          outerLength: sessionData.outer_length || 0,
          arReviewTemplateNames: sessionData.ar_review_template_names || [],
          selectedProducts: sessionData.selected_products || [],
        });
      } catch (err) {
        console.error("Error loading order details:", err);
      } finally {
        setLoading(false);
      }
    };

    loadOrderDetails();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#EF6F7"
        p={{ xs: 1, sm: 2 }}
      >
        <CircularProgress size={{ xs: 40, sm: 60 }} color="primary" />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#EF6F7"
      p={{ xs: 2, sm: 3, md: 4 }}
    >
      <Card
        elevation={10}
        sx={{
          maxWidth: { xs: "100%", sm: 600, md: 700 },
          width: "100%",
          borderRadius: 4,
          overflow: "hidden",
          border: "2px solid #B1727C",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Box textAlign="center" mb={{ xs: 3, sm: 4, md: 5 }}>
            <Celebration
              sx={{ fontSize: { xs: 48, sm: 64, md: 80 }, color: "#B1727C" }}
            />
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
                color: "#B1727C",
                fontWeight: "bold",
              }}
            >
              Order Confirmed!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                color: "#Fa54056",
              }}
            >
              Thank you for your order! We're excited to get started.
            </Typography>
          </Box>

          <Divider sx={{ mb: { xs: 3, sm: 4 }, borderColor: "#B1727C" }} />

          <Box mb={{ xs: 3, sm: 4 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                color: "#B1727C",
                fontWeight: "bold",
              }}
            >
              Order Summary
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#Fa54056",
                    fontWeight: "medium",
                  }}
                >
                  Order ID:
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#B1727C",
                    fontWeight: "bold",
                  }}
                >
                  {orderDetails.orderId}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#Fa54056",
                    fontWeight: "medium",
                  }}
                >
                  Date:
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#B1727C",
                    fontWeight: "bold",
                  }}
                >
                  {orderDetails.date}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#Fa54056",
                    fontWeight: "medium",
                  }}
                >
                  Customer:
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#B1727C",
                    fontWeight: "bold",
                  }}
                >
                  {orderDetails.firstName} {orderDetails.lastName}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#Fa54056",
                    fontWeight: "medium",
                  }}
                >
                  Email:
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#B1727C",
                    fontWeight: "bold",
                  }}
                >
                  {orderDetails.email}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#Fa54056",
                    fontWeight: "medium",
                  }}
                >
                  Mobile:
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#B1727C",
                    fontWeight: "bold",
                  }}
                >
                  {orderDetails.mobile}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: { xs: 3, sm: 4 }, borderColor: "#B1727C" }} />

          <Box mb={{ xs: 3, sm: 4 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                color: "#B1727C",
                fontWeight: "bold",
              }}
            >
              Lash Specifications
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#Fa54056",
                    fontWeight: "medium",
                  }}
                >
                  Lash Length:
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#B1727C",
                    fontWeight: "bold",
                  }}
                >
                  {orderDetails.lashLength} mm
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#Fa54056",
                    fontWeight: "medium",
                  }}
                >
                  Curl Type:
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#B1727C",
                    fontWeight: "bold",
                  }}
                >
                  {orderDetails.curlType}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#Fa54056",
                    fontWeight: "medium",
                  }}
                >
                  Thickness:
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#B1727C",
                    fontWeight: "bold",
                  }}
                >
                  {orderDetails.thickness}
                </Typography>
              </Box>
             
            </Box>
          </Box>

          <Divider sx={{ mb: { xs: 3, sm: 4 }, borderColor: "#B1727C" }} />

          <Box mb={{ xs: 3, sm: 4 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                color: "#B1727C",
                fontWeight: "bold",
              }}
            >
              AR Review Templates
            </Typography>
            {orderDetails.arReviewTemplateNames.length > 0 ? (
              <List dense>
                {orderDetails.arReviewTemplateNames.map((templateName, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: { xs: 28, sm: 36 } }}>
                      <CheckCircle
                        sx={{
                          fontSize: { xs: 18, sm: 22 },
                          color: "#B1727C",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={templateName}
                      primaryTypographyProps={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        color: "#B1727C",
                        fontWeight: "medium",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  color: "#Fa54056",
                }}
              >
                No AR review templates selected
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: { xs: 3, sm: 4 }, borderColor: "#B1727C" }} />

          <Box mb={{ xs: 3, sm: 4 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                color: "#B1727C",
                fontWeight: "bold",
              }}
            >
              Additional Products
            </Typography>
            {orderDetails.selectedProducts.length > 0 ? (
              <List dense>
                {orderDetails.selectedProducts.map((product) => (
                  <ListItem key={product.id} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: { xs: 28, sm: 36 } }}>
                      <CheckCircle
                        sx={{
                          fontSize: { xs: 18, sm: 22 },
                          color: "#B1727C",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={product.title}
                      primaryTypographyProps={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        color: "#B1727C",
                        fontWeight: "medium",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  color: "#Fa54056",
                }}
              >
                No additional products selected
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrderConfirmation;